import getIdentifier from "@/view/ProseMirrorNode/addAdditionalInfoToProseMirrorNode/utils/getIdentifier";
import getUpdatedNodeTypeCount from "@/view/ProseMirrorNode/addAdditionalInfoToProseMirrorNode/utils/getUpdatedNodeTypeCount";
import { INode } from "@/view/TiptapArticleBody/TiptapArticleBody.interface";

/**
 * Recursively adds a unique XPath-like identifier to each node in the ProseMirror document.
 *
 * This function traverses the given document structure, generating and assigning a `data-identifier`
 * attribute to each node based on its type and position relative to sibling nodes. It also removes
 * `ignore_block` nodes and flattens their content to ensure they do not interfere with XPath generation.
 *
 * @param rawText - The root node or subtree to process. Must conform to the `INode` structure.
 * @param parentIdentifier - (Optional) The identifier of the parent node, used to generate hierarchical identifiers.
 */
export const addXpathToNodes = (
  rawText: INode | null,
  parentIdentifier?: string,
) => {
  if (!rawText) return;

  // Removing 'ignore_block' nodes by flattening their content into the parent node's content array.
  rawText.content =
    rawText.content?.flatMap((child) =>
      child.type === "ignore_block" ? child.content || [] : child,
    ) || [];

  // Tracks the count of each node type within the current level of the document tree.
  let nodeTypeCount = {};

  // Iterates over child nodes while filtering out empty paragraphs.
  rawText.content
    .filter(
      (node) =>
        !(
          node.type === "paragraph" &&
          (!node.content || node.content.length === 0)
        ),
    )
    .forEach((node) => {
      // Updates the count of the current node type for identifier generation.
      nodeTypeCount = getUpdatedNodeTypeCount(node, nodeTypeCount);

      // Determines the unique identifier for the node, either from existing attributes or by generating one.
      const identifier =
        node.attrs?.["data-identifier"] ||
        getIdentifier(node, nodeTypeCount, parentIdentifier);

      // Recursively process child nodes, passing down the generated identifier as the parentIdentifier.
      if (node.content?.length) {
        addXpathToNodes(node, identifier);
      }

      // Assigns the computed identifier to the node's attributes.
      node.attrs = {
        ...node.attrs,
        "data-identifier": identifier || "",
      };
    });
};
